import React from 'react';
import { Coins, Gift, Mail, UserCog } from 'lucide-react';
import {
  DetailSheet,
  DetailSheetActions,
  DetailSheetHead,
  DetailSheetSection,
  DetailSheetValue,
  type DetailSheetActionItem,
} from '@/components/DetailSheet';
import { USER_ROLE_LABELS } from '@/utils/roles';
import { formatUserName } from '@/utils/formatters';
import { USER_STATUS, type AdminUser } from '@/types/user';
import { roleStyle } from './userBadges';
import styles from './AdminUsers.module.scss';

interface UserDetailSheetProps {
  user: AdminUser;
  isSelf: boolean;
  resending: boolean;
  onManagePackages: () => void;
  onAddCredits: () => void;
  onChangeRole: () => void;
  onResend: () => void;
}

const icon = (Icon: typeof Gift) => <Icon style={{ width: '1em', height: '1em' }} />;

export const UserDetailSheet: React.FC<UserDetailSheetProps> = ({
  user,
  isSelf,
  resending,
  onManagePackages,
  onAddCredits,
  onChangeRole,
  onResend,
}) => {
  const name = formatUserName(user.firstName, user.lastName);
  const active = user.status === USER_STATUS.ACTIVE;

  const actions: DetailSheetActionItem[] = [
    { key: 'packages', label: 'Manage packages', icon: icon(Gift), onClick: onManagePackages },
    { key: 'credits', label: 'Add credits', icon: icon(Coins), onClick: onAddCredits },
    {
      key: 'role',
      label: 'Change role',
      icon: icon(UserCog),
      onClick: onChangeRole,
      disabled: isSelf,
    },
    ...(!active && !isSelf
      ? [
          {
            key: 'resend',
            label: resending ? 'Sending…' : 'Resend invite',
            icon: icon(Mail),
            onClick: onResend,
            disabled: resending,
          },
        ]
      : []),
  ];

  return (
    <DetailSheet>
      <DetailSheetHead
        title={name || user.email}
        tags={
          <>
            <span className={`${styles.roleBadge} ${roleStyle(user.role)}`}>
              {USER_ROLE_LABELS[user.role]}
            </span>
            <span className={`${styles.statusBadge} ${active ? styles.active : styles.pending}`}>
              <span className={styles.dot} />
              {active ? 'Active' : 'Pending'}
            </span>
          </>
        }
      />

      <DetailSheetSection label="Email">
        <DetailSheetValue>{user.email}</DetailSheetValue>
      </DetailSheetSection>

      <DetailSheetSection label="Credits">
        <DetailSheetValue>{user.balance.toLocaleString()}</DetailSheetValue>
      </DetailSheetSection>

      <DetailSheetActions items={actions} />
    </DetailSheet>
  );
};

export default UserDetailSheet;
